import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sparkles } from 'lucide-react';

const Header = ({ sections }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px',
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sections]);

  const handleNavClick = (ref) => {
    setIsMobileMenuOpen(false);
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-[#0a0a1a]/90 backdrop-blur-xl shadow-2xl shadow-purple-500/10'
          : 'bg-transparent'
      }`}
    >
      {/* Magical top border */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background: 'linear-gradient(90deg, transparent, #6b4c9a, #14b8a6, #f59e0b, #14b8a6, #6b4c9a, transparent)'
        }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'linear'
        }}
      />

      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4 lg:py-5">
        {/* Logo */}
        <motion.button
          onClick={() => handleNavClick(sections[0].ref)}
          className="flex items-center gap-3 group"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="relative">
            <img
              src="/images/logo.png"
              alt="Ned and Nancy"
              className="w-auto h-10 md:h-14 object-contain brightness-0 invert"
            />
            {/* Sparkle effect on logo */}
            <motion.div
              className="absolute -top-1 -right-1"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
            </motion.div>
          </div>
        </motion.button>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-2">
          {sections.map(({ id, ref, label }, index) => (
            <motion.button
              key={id}
              custom={index}
              initial="hidden"
              animate="visible"
              variants={navVariants}
              onClick={() => handleNavClick(ref)}
              className="relative px-5 py-2.5 group"
            >
              {/* Background glow on hover */}
              <motion.div
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'radial-gradient(circle, rgba(107, 76, 154, 0.3) 0%, transparent 70%)'
                }}
              />

              {/* Active indicator */}
              {activeSection === id && (
                <motion.div
                  layoutId="activeNav"
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, rgba(107, 76, 154, 0.4) 0%, rgba(20, 184, 166, 0.3) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}

              <span className={`relative z-10 font-semibold text-sm tracking-wide transition-all duration-300 ${
                activeSection === id
                  ? 'text-amber-400'
                  : 'text-gray-300 group-hover:text-white'
              }`}>
                {label}
              </span>

              {/* Underline effect */}
              <motion.div
                className="absolute bottom-1 left-1/2 h-0.5 bg-gradient-to-r from-amber-400 to-teal-400"
                initial={{ width: 0, x: '-50%' }}
                whileHover={{ width: '60%' }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <motion.button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden relative w-12 h-12 flex items-center justify-center"
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            className="absolute inset-0 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(107, 76, 154, 0.3) 0%, rgba(20, 184, 166, 0.2) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          />
          <AnimatePresence mode="wait">
            {isMobileMenuOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6 text-amber-400" />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu className="w-6 h-6 text-gray-300" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="absolute top-full left-0 right-0 bg-[#0a0a1a]/95 backdrop-blur-xl border-t border-purple-500/20 md:hidden overflow-hidden"
            >
              {/* Background particles */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 rounded-full bg-amber-400/40"
                    style={{
                      left: `${20 + i * 15}%`,
                      top: `${30 + (i % 2) * 40}%`
                    }}
                    animate={{
                      opacity: [0.2, 0.6, 0.2],
                      scale: [1, 1.5, 1]
                    }}
                    transition={{
                      duration: 2 + i * 0.5,
                      repeat: Infinity,
                      delay: i * 0.3
                    }}
                  />
                ))}
              </div>

              <div className="flex flex-col items-center py-8 space-y-2">
                {sections.map(({ id, ref, label }, index) => (
                  <motion.button
                    key={id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleNavClick(ref)}
                    className={`relative w-4/5 py-4 rounded-2xl text-center font-semibold text-lg tracking-wide transition-all duration-300 ${
                      activeSection === id
                        ? 'text-amber-400'
                        : 'text-gray-300'
                    }`}
                  >
                    {activeSection === id && (
                      <motion.div
                        layoutId="activeMobileNav"
                        className="absolute inset-0 rounded-2xl"
                        style={{
                          background: 'linear-gradient(135deg, rgba(107, 76, 154, 0.3) 0%, rgba(20, 184, 166, 0.2) 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      />
                    )}
                    <span className="relative z-10">{label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;
